// Native port of the legacy mobile navigation public/js/custom/custom-side-menu.js.
// The jQuery original's behavior is preserved 1:1, with two deliberate
// exceptions:
// - Its `$(document).on("ready", ...)` block (Waves classes swapped around a
//   980px breakpoint) never executed under jQuery 3, which removed the
//   "ready" event alias, so it is intentionally NOT ported: porting it would
//   activate behavior production has never had.
// - The toggle gains equivalent-to-button keyboard semantics (Enter/Space,
//   Escape, aria-expanded/aria-controls), an intentional accessibility
//   improvement layered onto the unchanged pointer behavior.

const SLIDE_DURATION_MS = 600; // jQuery's 'slow'

declare global {
  interface Window {
    CUSTOM_SETTINGS?: { navClick?: () => void };
  }
}

const activeSlideTimers = new WeakMap<HTMLElement, number>();

function finishSlide(element: HTMLElement, display: string) {
  element.style.removeProperty("height");
  element.style.removeProperty("overflow");
  element.style.removeProperty("transition");
  element.style.display = display;
  activeSlideTimers.delete(element);
}

function isDisplayed(element: HTMLElement) {
  if (element.style.display === "none") {
    return false;
  }
  return getComputedStyle(element).display !== "none";
}

function slideUp(element: HTMLElement) {
  if (!isDisplayed(element)) {
    return;
  }
  window.clearTimeout(activeSlideTimers.get(element));
  element.style.height = `${element.scrollHeight}px`;
  element.style.overflow = "hidden";
  element.style.transition = `height ${SLIDE_DURATION_MS}ms ease`;
  requestAnimationFrame(() => {
    element.style.height = "0px";
  });
  activeSlideTimers.set(
    element,
    window.setTimeout(() => finishSlide(element, "none"), SLIDE_DURATION_MS),
  );
}

function slideDown(element: HTMLElement) {
  if (isDisplayed(element)) {
    return;
  }
  window.clearTimeout(activeSlideTimers.get(element));
  element.style.display = "block";
  const targetHeight = element.scrollHeight;
  element.style.height = "0px";
  element.style.overflow = "hidden";
  element.style.transition = `height ${SLIDE_DURATION_MS}ms ease`;
  requestAnimationFrame(() => {
    element.style.height = `${targetHeight}px`;
  });
  activeSlideTimers.set(
    element,
    window.setTimeout(() => finishSlide(element, "block"), SLIDE_DURATION_MS),
  );
}

function slideToggle(element: HTMLElement) {
  if (isDisplayed(element)) {
    slideUp(element);
  } else {
    slideDown(element);
  }
}

function menuContainer() {
  return document.querySelector(".utmenucontainer");
}

function isMenuOpen() {
  return menuContainer()?.classList.contains("wsoffcanvasopener") ?? false;
}

function syncToggleA11y() {
  const toggle = document.getElementById("wsnavtoggle");
  if (!toggle) {
    return;
  }
  toggle.setAttribute("role", "button");
  toggle.setAttribute("aria-expanded", String(isMenuOpen()));
  const menu = document.querySelector(".utmenu");
  if (menu) {
    if (!menu.id) {
      menu.id = "utmenu-navigation";
    }
    toggle.setAttribute("aria-controls", menu.id);
  }
}

function closeMenu() {
  for (const active of document.querySelectorAll(".utmenu-list .active")) {
    active.classList.remove("active");
  }
  for (const arrow of document.querySelectorAll(".utmenu-list .ws-activearrow")) {
    arrow.classList.remove("ws-activearrow");
  }
  for (const submenu of document.querySelectorAll<HTMLElement>(".utmenu-submenu")) {
    slideUp(submenu);
  }
  for (const container of document.querySelectorAll(".utmenucontainer")) {
    container.classList.remove("wsoffcanvasopener");
  }
  syncToggleA11y();
}

function toggleMenu() {
  if (isMenuOpen()) {
    closeMenu();
  } else {
    menuContainer()?.classList.add("wsoffcanvasopener");
    syncToggleA11y();
  }
}

function handleMouseup(event: MouseEvent) {
  const target = event.target;
  if (!(target instanceof Element)) {
    return;
  }
  if (target.closest("#wsnavtoggle")) {
    // jQuery `return false` on the delegated handler.
    event.preventDefault();
    toggleMenu();
    return;
  }
  if (target.closest(".utmenu")) {
    return;
  }
  closeMenu();
}

function handleMenuClick(event: MouseEvent) {
  const target = event.target;
  if (!(target instanceof Element)) {
    return;
  }
  const item = target.closest(".wsoffcanvasopener .utmenu-list li > a, .wsoffcanvasopener .utmenu-list li > span");
  const parent = item?.parentElement;
  if (!item || !parent) {
    return;
  }
  parent.classList.toggle("active");
  for (const child of parent.children) {
    if (child.tagName === "SPAN") {
      child.classList.toggle("ws-activearrow");
    }
    if (child instanceof HTMLElement && child.tagName === "UL") {
      slideToggle(child);
    }
  }

  // The legacy check treats any anchor whose href is not exactly "#" as a
  // real navigation (the shell's own toggles use href="# ", caught by the
  // overlay spans in mobile mode), and closes the menu behind it.
  if (item instanceof HTMLAnchorElement && item.getAttribute("href") !== "#") {
    closeMenu();
    window.setTimeout(() => {
      item.classList.remove("active");
    }, 1);
  }
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === "Escape" && isMenuOpen()) {
    const container = menuContainer();
    const active = document.activeElement;
    closeMenu();
    // Keep focus from being lost inside the closed off-canvas menu.
    if (active instanceof HTMLElement && container?.contains(active)) {
      document.getElementById("wsnavtoggle")?.focus();
    }
    return;
  }
  if (event.key !== "Enter" && event.key !== " ") {
    return;
  }
  const target = event.target;
  if (target instanceof Element && target.closest("#wsnavtoggle")) {
    event.preventDefault();
    toggleMenu();
  }
}

export function initLegacySideMenu(): void {
  const customSettings = window.CUSTOM_SETTINGS || {};
  window.CUSTOM_SETTINGS = customSettings;
  customSettings.navClick = () => {
    document.addEventListener("mouseup", handleMouseup);
  };
  customSettings.navClick();
  document.addEventListener("click", handleMenuClick);
  document.addEventListener("keydown", handleKeydown);

  // The toggle is rendered by React after this module runs; stamp the
  // accessibility attributes as soon as it appears (and again after any
  // re-mount).
  const observer = new MutationObserver(() => {
    if (typeof document === "undefined") {
      // The jsdom test environment tears down the DOM before pending
      // observer callbacks flush.
      return;
    }
    const toggle = document.getElementById("wsnavtoggle");
    if (toggle && !toggle.hasAttribute("aria-expanded")) {
      syncToggleA11y();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}
